import React, { useEffect, useState } from 'react';
import { Box, Chip, Grid, Tab, Tabs, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { filter, isEmpty, size } from 'lodash';
import CustomCard2 from '../../components/cards/CustomCard2';
import ProductsSidebar from '../../components/sidebars/ProductsSidebar';
import PageLayout from '../layouts/PageLayout';
import PropTypes from 'prop-types';
import { colors } from '../../assets/utils/colors';
import ProductWidget from '../../components/widgets/ProductWidget';

// RTK query imports and redux
import NoDataFeedback from '../../components/feedbacks/NoDataFeedback';
import { productsSelector } from '../../features/selectors';
import CountBadge from '../../components/CountBadge';

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 1 }}>{children}</Box>}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        'id': `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

// !############### MAIN FUNC ####################
const Products = () => {
    // ############## Comp state ###################
    const [value, setValue] = React.useState(0);
    const [onSellProducts, setOnSellProducts] = useState([]);
    const [outOfStockProducts, setOutOfStockProducts] = useState([]);

    // ############## CONST #######################

    // ############## Redux state #######################
    const { products } = useSelector(productsSelector);

    // ############## FUNC #######################
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };
    // => Get on-sell products
    const getOnSellProducts = () => {
        const onSellProducts = filter(
            products,
            ({ product_details }) => product_details.isOnSale === 1,
        );
        setOnSellProducts(onSellProducts);
    };

    // => Get out of stock products
    const getOutOfStockProducts = () => {
        const outOfStockProducts = filter(
            products,
            ({ product_details }) =>
                product_details.product_quantity -
                    product_details.product_sold ===
                0,
        );
        setOutOfStockProducts(outOfStockProducts);
    };

    // ############## UseEffect #######################
    useEffect(() => {
        let isSubscribed = true;
        if (isSubscribed) {
            getOnSellProducts();
            getOutOfStockProducts();
        }
        return () => {
            isSubscribed = false;
        };
    }, []);

    return (
        <>
            <PageLayout
                sidebar={<ProductsSidebar />}
                head="Products managements"
            >
                <CustomCard2
                    title={'CAICA products'}
                    heightClear={640}
                    action={
                        <CountBadge total={size(products)} item={'Products'} />
                    }
                >
                    <Box sx={{ width: '100%' }}>
                        <Box
                            sx={{
                                borderBottom: 1,
                                borderColor: 'divider',
                                position: 'sticky',
                                top: 0,
                                background: `linear-gradient(180deg, ${colors.white}, ${colors.white})`,
                                zIndex: 999,
                            }}
                        >
                            <Tabs
                                value={value}
                                onChange={handleChange}
                                aria-label="basic tabs example"
                            >
                                <Tab
                                    label={
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    fontWeight: 'bold',
                                                    mr: 1,
                                                }}
                                            >
                                                All
                                            </Typography>
                                            <Chip
                                                sx={{ fontSize: 10 }}
                                                label={size(products)}
                                                color="info"
                                                size="small"
                                            />
                                        </Box>
                                    }
                                    {...a11yProps(0)}
                                />
                                <Tab
                                    label={
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    fontWeight: 'bold',
                                                    mr: 1,
                                                }}
                                            >
                                                On-sell
                                            </Typography>
                                            <Chip
                                                sx={{ fontSize: 10 }}
                                                label={size(onSellProducts)}
                                                color="warning"
                                                size="small"
                                            />
                                        </Box>
                                    }
                                    {...a11yProps(1)}
                                />
                                <Tab
                                    label={
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    fontWeight: 'bold',
                                                    mr: 1,
                                                }}
                                            >
                                                Out-stock
                                            </Typography>
                                            <Chip
                                                sx={{ fontSize: 10 }}
                                                label={size(outOfStockProducts)}
                                                color="error"
                                                size="small"
                                            />
                                        </Box>
                                    }
                                    {...a11yProps(2)}
                                />
                            </Tabs>
                        </Box>
                        <TabPanel value={value} index={0}>
                            <Grid container spacing={3} padding={2}>
                                {isEmpty(products) ? (
                                    <NoDataFeedback message="No product found" />
                                ) : (
                                    products.map((product, index) => (
                                        <Grid item sm={3} key={index}>
                                            <ProductWidget
                                                product={
                                                    product.product_details
                                                }
                                            />
                                        </Grid>
                                    ))
                                )}
                            </Grid>
                        </TabPanel>
                        <TabPanel value={value} index={1}>
                            <Grid container spacing={3} padding={2}>
                                {isEmpty(onSellProducts) ? (
                                    <NoDataFeedback message="No product found" />
                                ) : (
                                    onSellProducts.map((product, index) => (
                                        <Grid item sm={3} key={index}>
                                            <ProductWidget
                                                product={
                                                    product.product_details
                                                }
                                            />
                                        </Grid>
                                    ))
                                )}
                            </Grid>
                        </TabPanel>
                        <TabPanel value={value} index={2}>
                            <Grid container spacing={3} padding={2}>
                                {isEmpty(outOfStockProducts) ? (
                                    <NoDataFeedback message="No product found" />
                                ) : (
                                    outOfStockProducts.map((product, index) => (
                                        <Grid item sm={3} key={index}>
                                            <ProductWidget
                                                product={
                                                    product.product_details
                                                }
                                            />
                                        </Grid>
                                    ))
                                )}
                            </Grid>
                        </TabPanel>
                    </Box>
                </CustomCard2>
            </PageLayout>
        </>
    );
};

export default Products;
